import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Segmented, Card, Space, Spin, Result, Button, Typography, Tooltip, Empty } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";

import StatusOverview from "./StatusOverview";
import { fetchStatus } from "../api";

const { Text } = Typography;

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

const MainPage = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [emptyData, setEmptyData] = useState(false);
  const navigate = useNavigate();
  const { environment } = useParams();
  const [selectedEnvironment, setSelectedEnvironment] = useState(environment || "development");
  const { instance, accounts } = useMsal();

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    setEmptyData(false);
    
    try {
      const data = await fetchStatus(selectedEnvironment, instance, accounts);
      
      if (!data || Object.keys(data).length === 0) {
        setEmptyData(true);
        setData({});
      } else {
        setData(data);
        setEmptyData(false);
      }
      
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Error fetching status:", error);
      setLoading(false);
      setError(true);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [selectedEnvironment, instance, accounts]);

  // Auto refresh every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData();
    }, REFRESH_INTERVAL);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [selectedEnvironment, instance, accounts]);

  const handleEnvironmentChange = (value) => {
    setSelectedEnvironment(value);
    navigate(`/${value}`);
  };

  const formatLastUpdated = (date) => {
    if (!date) return "Unknown";
    try {
      return date.toLocaleString("en-US", {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <Result
          status="500"
          title="500"
          subTitle="Sorry, something went wrong with the server."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          }
        />
      );
    }

    if (emptyData) {
      return (
        <Empty
          description={
            <span style={{ color: "white" }}>
              No data available for {selectedEnvironment} environment
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ 
            marginTop: 50,
            color: "white"
          }}
        />
      );
    }

    return (
      Object.keys(data).length > 0 ? (
        Object.keys(data).map((client) => (
          <div key={client} style={{ textAlign: "center", marginBottom: "20px" }}>
            <div style={{ display: "inline-block", width: "100%" }}>
              {Object.keys(data[client]).map((project, index, arr) => (
                <StatusOverview
                  key={project}
                  projectName={project}
                  domain={selectedEnvironment}
                  executionData={data[client][project]["executions"]}
                  overallExecutionData={data[client][project]["overall"]}
                  isFirst={index === 0}
                  isLast={index === arr.length - 1}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <Empty
          description={
            <span style={{ color: "white" }}>
              No data available for {selectedEnvironment} environment
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ 
            marginTop: 50,
            color: "white"
          }}
        />
      )
    );
  };

  return (
    <div style={{ backgroundColor: "#121212", minHeight: "100vh", padding: "20px" }}>
      <h1 style={{ textAlign: "center", color: "#ffffff" }}>Flyte Status</h1>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <Space direction="vertical" style={{ display: "flex", alignItems: "center" }}>
          <Card style={{ width: 400 }}>
            <Segmented
              block
              options={[
                { label: "Development", value: "development" },
                { label: "Staging", value: "staging" },
                { label: "Production", value: "production" },
              ]}
              value={selectedEnvironment}
              onChange={handleEnvironmentChange}
            />
          </Card>
        </Space>
      </div>

      {renderContent()}
    </div>
  );
};

export default MainPage;
